import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CaretLeft, CaretRight, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { AnnualCalendarEmpty } from '../../components/calendar/AnnualCalendarEmpty';
import { AnnualMonthDetail } from '../../components/calendar/AnnualMonthDetail';
import { AnnualPlanCard } from '../../components/calendar/AnnualPlanCard';
import { AnnualYearGrid } from '../../components/calendar/AnnualYearGrid';
import { AnnualFormDialog } from '../../components/settings/AnnualFormDialog';
import { Button } from '../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog';
import {
  StickyPageAction,
  StickyPageActionButton,
} from '../../components/layout/StickyPageAction';
import { MOBILE_PAGE_PADDING_WITH_STICKY_CLASS } from '../../components/layout/mobileLayoutConstants';
import { useAnnualCalendarQuery } from '../../features/calendar/queries';
import {
  useCreateAnnualOccasionalMutation,
  useDeleteAnnualOccasionalMutation,
  useUpdateAnnualOccasionalMutation,
} from '../../features/expenses/queries';
import { useCreatePotFromOccasionalMutation } from '../../features/pots/queries';
import {
  buildMonthGridMeta,
  defaultSelectedMonth,
  isCalendarEmpty,
} from '../../lib/annualCalendarUtils';
import { suggestionToFormInitial } from '../../lib/annualOccasionalSuggestions';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { PageContentShell } from '../../components/common/PageContentShell';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import pt from '../../locales/pt';
import { saveAnnualOccasional } from '../../utils/saveAnnualOccasional';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { useAuth } from '../../store/AuthContext';
import { isEditableAnnualMonth } from '../../utils/editableCycle';
import { cn } from '../../utils/cn';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';

/** `.pen` → `ds/nav/year-switcher` (master `gu4vu`). */
function YearSwitcher({ year, onPrev, onNext, className }) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-space-1 rounded-ds-full border border-border bg-card p-space-1 shadow-sm',
        className,
      )}
      data-testid="calendar-year-switcher"
    >
      <button
        type="button"
        onClick={onPrev}
        aria-label="Ano anterior"
        className="flex h-8 w-8 items-center justify-center rounded-ds-full bg-muted text-foreground transition-colors hover:bg-sage-tint"
      >
        <CaretLeft className="h-4 w-4" weight="bold" aria-hidden />
      </button>
      <span className="min-w-[4.5rem] px-space-1 text-center font-display text-type-body font-bold tabular-nums text-foreground">
        {year}
      </span>
      <button
        type="button"
        onClick={onNext}
        aria-label="Ano seguinte"
        className="flex h-8 w-8 items-center justify-center rounded-ds-full bg-muted text-foreground transition-colors hover:bg-sage-tint"
      >
        <CaretRight className="h-4 w-4" weight="bold" aria-hidden />
      </button>
    </div>
  );
}

export function AnnualCalendar() {
  const location = useLocation();
  const { profile } = useAuth();
  const today = useMemo(() => new Date(), []);
  const currentYear = useMemo(() => today.getFullYear(), [today]);
  const [year, setYear] = useState(currentYear);
  const selfieDay = profile?.selfie_day_of_month;
  const { cycles } = useSelfieCycle();
  const gridRef = useRef(null);

  const isMonthEditable = useCallback(
    (viewYear, month) => isEditableAnnualMonth(viewYear, month, selfieDay, today, cycles),
    [selfieDay, today, cycles],
  );

  const [dialogMonth, setDialogMonth] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isPending, isFetching } = useAnnualCalendarQuery(year);
  const calendarShell = useQueryShellState({ data, isPending, isFetching });
  const createPot = useCreatePotFromOccasionalMutation();
  const createAnnual = useCreateAnnualOccasionalMutation();
  const updateAnnual = useUpdateAnnualOccasionalMutation();
  const deleteAnnual = useDeleteAnnualOccasionalMutation();

  const months = useMemo(() => data?.months || [], [data]);
  const monthsMeta = useMemo(() => buildMonthGridMeta(months), [months]);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    if (!data) return;
    setSelectedMonth((prev) => {
      if (prev != null && data.year === year) return prev;
      return defaultSelectedMonth(year, data.months || [], today);
    });
  }, [year, data, today]);

  useEffect(() => {
    const month = location.state?.month;
    const stateYear = location.state?.year;
    if (!month) return;
    if (stateYear && stateYear !== year) {
      setYear(stateYear);
    }
    setSelectedMonth(Number(month));
  }, [location.state, year]);

  const selectedMonthData = useMemo(
    () => months.find((month) => month.month === selectedMonth) ?? null,
    [months, selectedMonth],
  );

  const showEmptyState = !calendarShell.showBlockingSkeleton && isCalendarEmpty(data);
  const showPopulated = !calendarShell.showBlockingSkeleton && data && !showEmptyState;

  const handleCreatePot = (occasionalId) => {
    createPot.mutate(occasionalId, {
      onSuccess: () => toast.success(pt.calendar.createPot),
      onError: (err) => {
        const message =
          (err && (err.detail || JSON.stringify(err))) || 'Erro inesperado.';
        toast.error(message);
      },
    });
  };

  const openCreateDialog = (month, initial = null) => {
    setEditing(null);
    setDialogInitial(initial);
    setDialogMonth(month ?? selectedMonth ?? today.getMonth() + 1);
  };

  const handleAddSuggestion = (suggestion) => {
    setEditing(null);
    setDialogInitial(suggestionToFormInitial(suggestion, year));
    setDialogMonth(suggestion.month);
  };

  const submitAnnual = async (payload) => {
    const withPot = Boolean(payload.create_smoothing_pot);
    try {
      if (editing?.id) {
        await updateAnnual.mutateAsync({ id: editing.id, payload });
        toast.success(pt.settings.annual.toast.updated);
        if (withPot) {
          toast.success(pt.calendar.toast.potCreated);
        }
      } else {
        await saveAnnualOccasional({
          payload,
          createAnnual: (data) => createAnnual.mutateAsync(data),
        });
        toast.success(pt.settings.annual.toast.created);
        if (withPot) {
          toast.success(pt.calendar.toast.potCreated);
        }
      }
      setDialogMonth(null);
      setEditing(null);
      setDialogInitial(null);
    } catch (err) {
      toast.error((err && (err.detail || JSON.stringify(err))) || 'Erro.');
    }
  };

  const handleEditItem = (item, month) => {
    setDialogInitial(null);
    setEditing({
      id: item.occasional_id,
      name: item.name,
      budget_allocation: item.budget_allocation,
      amount: item.amount,
      month,
      day_of_month: item.day_of_month ?? 1,
      target_year: item.target_year ?? null,
      pot_id: item.pot_id,
      has_pot: item.has_pot ?? Boolean(item.pot_id),
    });
    setDialogMonth(month);
  };

  const handleDeleteItem = (item) => {
    setDeleting(item);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteAnnual.mutateAsync(deleting.occasional_id);
      toast.success('Removido.');
      setDeleting(null);
    } catch (err) {
      toast.error((err && (err.detail || JSON.stringify(err))) || 'Não foi possível apagar.');
    }
  };

  const handleViewYear = () => {
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const dialogInitialValue =
    editing ||
    dialogInitial ||
    (dialogMonth != null ? { month: dialogMonth, target_year: year } : null);

  return (
    <div
      className={cn(
        'flex flex-col gap-space-5',
        showPopulated && MOBILE_PAGE_PADDING_WITH_STICKY_CLASS,
      )}
    >
      <GuidedFlowReturnBanner />

      <div className="flex flex-col items-center gap-space-2 md:hidden">
        <YearSwitcher
          year={year}
          onPrev={() => setYear((y) => y - 1)}
          onNext={() => setYear((y) => y + 1)}
        />
        <p className="type-body-sm text-center text-muted-foreground">{pt.calendar.subtitle}</p>
      </div>

      <header className="hidden items-center justify-between gap-space-4 md:flex">
        <div className="min-w-0 flex-1">
          <h1 className="type-title-page text-foreground">{pt.calendar.title}</h1>
          <p className="type-body-sm mt-0.5 text-muted-foreground">{pt.calendar.subtitle}</p>
        </div>
        <YearSwitcher
          year={year}
          onPrev={() => setYear((y) => y - 1)}
          onNext={() => setYear((y) => y + 1)}
        />
      </header>

      <PageContentShell
        shells={[calendarShell]}
        fallback={
          <div className="flex flex-col gap-space-5" aria-busy="true">
            <SectionSkeleton variant="hero" />
            <SectionSkeleton variant="card-grid" />
          </div>
        }
      >
        {showEmptyState ? (
          <div className="flex justify-center md:mx-auto md:w-full md:max-w-[560px] lg:max-w-[640px]">
            <AnnualCalendarEmpty
              onAddSuggestion={handleAddSuggestion}
              onAddCustom={() => openCreateDialog(today.getMonth() + 1)}
            />
          </div>
        ) : null}

        {showPopulated ? (
          <div className="grid grid-cols-1 gap-space-5 lg:grid-cols-[1.55fr_1fr] lg:items-start">
            <div className="flex flex-col gap-space-5 lg:col-start-1">
              <AnnualPlanCard data={data} />
              <AnnualYearGrid
                gridRef={gridRef}
                monthsMeta={monthsMeta}
                selectedMonth={selectedMonth}
                onSelectMonth={setSelectedMonth}
              />
            </div>
            <div className="lg:col-start-2">
              <AnnualMonthDetail
                month={selectedMonthData}
                selectedMonth={selectedMonth}
                canEdit={isMonthEditable(year, selectedMonth)}
                onCreatePotFor={handleCreatePot}
                onEditItem={handleEditItem}
                onDeleteItem={handleDeleteItem}
                onViewYear={handleViewYear}
                onAddInMonth={(month) => openCreateDialog(month)}
              />
            </div>
          </div>
        ) : null}
      </PageContentShell>

      {showPopulated ? (
        <>
          <StickyPageAction>
            <StickyPageActionButton onClick={() => openCreateDialog(selectedMonth)}>
              <Plus className="h-4 w-4" aria-hidden />
              {pt.calendar.fab.create}
            </StickyPageActionButton>
          </StickyPageAction>
          <Button
            type="button"
            onClick={() => openCreateDialog(selectedMonth)}
            className="fixed bottom-6 right-6 z-20 hidden md:inline-flex"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {pt.calendar.fab.create}
          </Button>
        </>
      ) : null}

      <AnnualFormDialog
        variant="calendar"
        viewYear={year}
        isOpen={dialogMonth != null}
        onClose={() => {
          setDialogMonth(null);
          setEditing(null);
          setDialogInitial(null);
        }}
        onSubmit={submitAnnual}
        initial={dialogInitialValue}
        isPending={
          createAnnual.isPending ||
          updateAnnual.isPending ||
          deleteAnnual.isPending ||
          createPot.isPending
        }
      />

      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(openDialog) => {
          if (!openDialog) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.calendar.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>{pt.calendar.deleteConfirmBody}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              {pt.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteAnnual.isPending}
            >
              {deleteAnnual.isPending ? pt.common.loading : pt.calendar.deleteConfirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AnnualCalendar;
